import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

// ====================================================================
// RealisationsPage                                     v22  Stage S5
// --------------------------------------------------------------------
// What changed:
//
//  B12  Forward contracts are now chosen by BANK. Until a bank is
//       picked the list is empty and says why, and changing the bank
//       reloads it. Before this, none of the three endpoints filtered
//       by bank at all — every bank's contracts were offered, and the
//       page tried to narrow them with a loose text match that let
//       "hdfc" match anything containing those letters.
//
//  B13  The server re-checks every forward and every PCFC loan on the
//       way in. This page simply shows what it says.
//
//  B14  A bill with no currency explains itself instead of showing an
//       empty list with no reason.
//
//  B16  Negative amounts are refused.
//
//  B10  Error pop-ups carry the real reason from the server.
//
//  EEFC A "Keep in EEFC" bucket. The balance rule becomes:
//
//         Spot + Forward + PCFC + Loan repayment + EEFC
//                                        =  Total FC received
//
//       Shortfall is NOT a bucket — it stays what it always was, the
//       invoice amount less everything actually received, worked out
//       at final settlement.
//
//       The parked money HAS been received, so the bill is settled by
//       it. What it has not done is been converted, so it has no rate
//       yet: the weighted average rate and the exchange difference
//       cover the CONVERTED part only (rule R11).
//
//       Converting that money later happens on the EEFC page, not
//       here — a conversion is not a realisation.
// ====================================================================

const fmt = (n, dp = 2) =>
  n === null || n === undefined || n === '' || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp });

const fmt0 = (n) =>
  n === null || n === undefined || n === '' || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const rate4 = (n) => (Number(n) > 0 ? Number(n).toFixed(4) : '—');

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const ccyCode   = (c) => (c && c.length === 6 ? c.slice(0, 3) : (c || ''));
const pairLabel = (c) =>
  c && c.length === 6 ? `${c.slice(0, 3)}/${c.slice(3)}` : (c || '—');

const serverMessage = (err, fallback) => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d.trim()) return d;
  if (d && typeof d.title === 'string') return d.title;
  if (d && typeof d.message === 'string') return d.message;
  return fallback;
};

// The forward endpoints return { forwards, reason }. Older builds
// returned a bare array, so both shapes are accepted.
const readForwards = (data) => {
  if (Array.isArray(data)) return { list: data, reason: null };
  return { list: data?.forwards || [], reason: data?.reason || null };
};

const emptyForm = {
  remittanceType: 'NORMAL',
  customerCode: '', bank: '', invoiceId: '', orderId: '',
  realisationDate: '', totalFcRealised: '',
  spotFc: '', spotRate: '',
  discountedFc: '', loanRepaymentRate: '',
  eefcFc: '', eefcAccountId: '', benchmarkRate: '',
  isFinalSettlement: false, notes: ''
};

const noteStyle = { fontSize: '12px', color: '#64748B' };

const warnBox = {
  background: '#FEF9C3', border: '1px solid #FDE68A',
  borderRadius: '6px', padding: '8px 12px',
  fontSize: '12px', color: '#92400E', marginBottom: '10px'
};

const infoBox = {
  background: '#F0F9FF', border: '1px solid #BAE6FD',
  borderRadius: '6px', padding: '10px 14px',
  fontSize: '13px', marginBottom: '14px'
};

const smallInput = {
  width: '110px', padding: '4px 8px',
  border: '1px solid #CBD5E1',
  borderRadius: '4px', fontSize: '13px'
};

function RealisationsPage({ token }) {
  const auth = { headers: { authorization: `Bearer ${token}` } };

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);

  const [availableForwards, setAvailableForwards] = useState([]);
  const [forwardReason, setForwardReason] = useState(null);
  const [forwardAllocations, setForwardAllocations] = useState([]);

  const [availablePcfc, setAvailablePcfc] = useState([]);
  const [pcfcAllocations, setPcfcAllocations] = useState([]);

  const [eefcContext, setEefcContext] = useState(null);
  const [creatingLedger, setCreatingLedger] = useState(false);

  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterSettlement, setFilterSettlement] = useState('');
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadRecords();
    loadCustomers(false);
    loadBanks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // loaders
  // ------------------------------------------------------------------
  const loadRecords = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CONFIG.API}/api/bills/realisations`, auth);
      setRecords(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async (advanceOnly = false) => {
    try {
      const url = `${CONFIG.API}/api/bills/realisations/customers` +
        (advanceOnly ? '?advanceOnly=true' : '');
      const res = await axios.get(url, auth);
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadBanks = async () => {
    try {
      const res = await axios.get(`${CONFIG.API}/api/bills/banks`, auth);
      setBanks(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadInvoices = async (customerCode) => {
    if (!customerCode) { setInvoices([]); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/realisations/invoices/${customerCode}`, auth);
      setInvoices(res.data);
    } catch (err) {
      console.error(err);
      setInvoices([]);
    }
  };

  const loadOrders = async (customerCode) => {
    if (!customerCode) { setOrders([]); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/realisations/orders/${customerCode}`, auth);
      setOrders(res.data);
    } catch (err) {
      console.error(err);
      setOrders([]);
    }
  };

  // B12 — the bank is part of the question now. No bank, no forwards.
  const loadAvailableForwards = async (invoiceId, orderId, date, bank) => {
    if ((!invoiceId && !orderId)) {
      setAvailableForwards([]);
      setForwardReason(null);
      return;
    }
    if (!bank) {
      setAvailableForwards([]);
      setForwardReason(
        'Choose the bank first. A forward contract can only be used at its own bank.');
      return;
    }
    if (!date) {
      setAvailableForwards([]);
      setForwardReason(
        'Enter the realisation date first — a forward\'s window has to cover it.');
      return;
    }
    try {
      const url = invoiceId
        ? `${CONFIG.API}/api/bills/realisations/available-forwards-date/${invoiceId}/${date}`
        : `${CONFIG.API}/api/bills/realisations/available-forwards-order/${orderId}/${date}`;
      const res = await axios.get(url, { ...auth, params: { bank } });
      const { list, reason } = readForwards(res.data);
      setAvailableForwards(list);
      setForwardReason(reason);
    } catch (err) {
      console.error(err);
      setAvailableForwards([]);
      setForwardReason(serverMessage(err, 'Could not load forward contracts.'));
    }
  };

  const loadAvailablePcfc = async (invoiceId, orderId, bank) => {
    if (!bank || (!invoiceId && !orderId)) { setAvailablePcfc([]); return; }
    try {
      const url = invoiceId
        ? `${CONFIG.API}/api/bills/realisations/available-pcfc/${invoiceId}`
        : `${CONFIG.API}/api/bills/realisations/available-pcfc-order/${orderId}`;
      const res = await axios.get(url, { ...auth, params: { bank } });
      setAvailablePcfc(res.data);
    } catch (err) {
      console.error(err);
      setAvailablePcfc([]);
    }
  };

  const loadEefcContext = async (invoiceId, orderId, bank, date) => {
    if (!bank || (!invoiceId && !orderId)) { setEefcContext(null); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/realisations/eefc-context`,
        { ...auth, params: {
            invoiceId: invoiceId || '', orderId: orderId || '',
            bank, date: date || ''
        } });
      setEefcContext(res.data);
    } catch (err) {
      console.error(err);
      setEefcContext(null);
    }
  };

  // For a discounted invoice — auto-fill the loan repayment (read-only).
  const loadDiscountingSummary = async (invoiceId) => {
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/realisations/discounting-summary/${invoiceId}`, auth);
      const fc   = num(res.data.fcDiscounted);
      const rate = num(res.data.discountRate);
      setForm(prev => ({
        ...prev,
        discountedFc:      fc > 0 ? String(fc)   : '',
        loanRepaymentRate: fc > 0 ? String(rate) : ''
      }));
    } catch (err) {
      console.error(err);
    }
  };

  // Everything that depends on bill + bank + date, in one place, so no
  // combination can be missed.
  const refreshLists = async (next) => {
    const invId = next.remittanceType === 'NORMAL' ? next.invoiceId : null;
    const ordId = next.remittanceType === 'ADVANCE' ? next.orderId : null;

    await loadAvailableForwards(invId, ordId, next.realisationDate, next.bank);
    await loadAvailablePcfc(invId, ordId, next.bank);
    await loadEefcContext(invId, ordId, next.bank, next.realisationDate);
  };

  // ------------------------------------------------------------------
  // form handling
  // ------------------------------------------------------------------
  const clearAllocations = () => {
    setForwardAllocations([]);
    setPcfcAllocations([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = { ...form, [name]: value };

    if (name === 'remittanceType') {
      next = {
        ...emptyForm,
        remittanceType: value,
        bank: form.bank,
        realisationDate: form.realisationDate
      };
      setForm(next);
      clearAllocations();
      setAvailableForwards([]); setForwardReason(null);
      setAvailablePcfc([]); setEefcContext(null);
      setInvoices([]); setOrders([]);
      loadCustomers(value === 'ADVANCE');
      return;
    }

    if (name === 'customerCode') {
      next = {
        ...next, invoiceId: '', orderId: '',
        discountedFc: '', loanRepaymentRate: '',
        eefcFc: '', eefcAccountId: '', isFinalSettlement: false
      };
      setForm(next);
      clearAllocations();
      setAvailableForwards([]); setForwardReason(null);
      setAvailablePcfc([]); setEefcContext(null);
      if (next.remittanceType === 'NORMAL') loadInvoices(value);
      else loadOrders(value);
      return;
    }

    if (name === 'invoiceId') {
      next = { ...next, eefcFc: '', eefcAccountId: '' };
      setForm(next);
      clearAllocations();
      if (value) loadDiscountingSummary(value);
      refreshLists(next);
      return;
    }

    if (name === 'orderId') {
      next = { ...next, eefcFc: '', eefcAccountId: '' };
      setForm(next);
      clearAllocations();
      refreshLists(next);
      return;
    }

    // B12 — this is the reload that was missing altogether.
    if (name === 'bank') {
      setForm(next);
      clearAllocations();
      refreshLists(next);
      return;
    }

    if (name === 'realisationDate') {
      setForm(next);
      setForwardAllocations([]);
      refreshLists(next);
      return;
    }

    setForm(next);
  };

  const handleForwardAllocationChange = (forwardId, value) => {
    const existing = forwardAllocations.find(a => a.forwardId === forwardId);
    if (existing) {
      setForwardAllocations(forwardAllocations.map(a =>
        a.forwardId === forwardId ? { ...a, fcAllocated: value } : a));
    } else {
      const fwd = availableForwards.find(f => f.forwardId === forwardId);
      setForwardAllocations([...forwardAllocations, {
        forwardId, fcAllocated: value, forwardRate: fwd?.forwardRate || 0
      }]);
    }
  };

  const handlePcfcAllocationChange = (pcfcId, value) => {
    const existing = pcfcAllocations.find(a => a.pcfcId === pcfcId);
    if (existing) {
      setPcfcAllocations(pcfcAllocations.map(a =>
        a.pcfcId === pcfcId ? { ...a, fcAllocated: value } : a));
    } else {
      setPcfcAllocations([...pcfcAllocations, { pcfcId, fcAllocated: value }]);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    clearAllocations();
    setAvailableForwards([]); setForwardReason(null);
    setAvailablePcfc([]); setEefcContext(null);
    setInvoices([]); setOrders([]);
    setEditingId(null);
    loadCustomers(false);
    setShowForm(false);
  };

  // ------------------------------------------------------------------
  // create a missing EEFC ledger without leaving the page
  // ------------------------------------------------------------------
  const handleCreateLedger = async () => {
    if (!eefcContext?.bankId || !eefcContext?.currency) return;
    if (!window.confirm(
      `Create a ${pairLabel(eefcContext.currency)} EEFC ledger at ${form.bank}?\n\n`
    + 'It starts with a zero balance. You can add an opening balance later on the Bank page.'
    )) return;

    try {
      setCreatingLedger(true);
      await axios.post(
        `${CONFIG.API}/api/bills/banks/${eefcContext.bankId}/eefc`,
        {
          currency: eefcContext.currency,
          accountNo: null, openingFc: 0, openingRate: 0, openingDate: null,
          notes: 'Created from the realisation page'
        }, auth);
      await loadEefcContext(
        form.remittanceType === 'NORMAL' ? form.invoiceId : null,
        form.remittanceType === 'ADVANCE' ? form.orderId : null,
        form.bank, form.realisationDate);
    } catch (err) {
      alert(serverMessage(err, 'Could not create the EEFC ledger.'));
    } finally {
      setCreatingLedger(false);
    }
  };

  // ------------------------------------------------------------------
  // derived
  // ------------------------------------------------------------------
  const selectedInvoice = invoices.find(
    i => String(i.invoiceId) === String(form.invoiceId));

  const selectedOrder = orders.find(
    o => String(o.orderId) === String(form.orderId));

  const billCurrency =
    selectedInvoice?.currency || selectedOrder?.currency || eefcContext?.currency || '';

  const isFullyDiscounted = selectedInvoice &&
    num(selectedInvoice.fcDiscounted) >= num(selectedInvoice.fcAmount);
  const isPartlyDiscounted = selectedInvoice &&
    num(selectedInvoice.fcDiscounted) > 0 &&
    num(selectedInvoice.fcDiscounted) < num(selectedInvoice.fcAmount);
  const isNotDiscounted = selectedInvoice &&
    num(selectedInvoice.fcDiscounted) === 0;

  // The loan repayment closes the discounted portion, capped at what was
  // actually received. If less came in than was discounted, the gap is
  // the shortfall and must NOT be forced into the allocation.
  const discountedFcNum = num(form.discountedFc);
  const receivedFcNum   = num(form.totalFcRealised);
  const loanRepaymentFc = receivedFcNum > 0
    ? Math.min(discountedFcNum, receivedFcNum)
    : discountedFcNum;

  const calcTotals = () => {
    const spotFc  = num(form.spotFc);
    const eefcFc  = num(form.eefcFc);
    const loanFc  = loanRepaymentFc;

    const forwardFc = forwardAllocations
      .reduce((s, a) => s + num(a.fcAllocated), 0);
    const pcfcFc = pcfcAllocations
      .reduce((s, a) => s + num(a.fcAllocated), 0);

    const convertedFc    = spotFc + forwardFc + pcfcFc;
    const totalAllocated = convertedFc + eefcFc + loanFc;
    const fcToRealise    = num(form.totalFcRealised);
    const remaining      = fcToRealise - totalAllocated;

    // The rate covers the CONVERTED part only (rule R11). The loan
    // repayment is excluded because its gain was locked at discounting;
    // the EEFC part because it has no rate yet.
    let weightedSum = 0;
    if (spotFc > 0 && num(form.spotRate) > 0) {
      weightedSum += spotFc * num(form.spotRate);
    }
    forwardAllocations.forEach(a => {
      const fc = num(a.fcAllocated);
      if (fc > 0) weightedSum += fc * num(a.forwardRate);
    });
    pcfcAllocations.forEach(a => {
      const fc = num(a.fcAllocated);
      if (fc > 0) {
        const loan = availablePcfc.find(p => p.pcfcId === a.pcfcId);
        weightedSum += fc * (loan ? num(loan.drawdownRate) : 0);
      }
    });

    const weightedAvg = convertedFc > 0 ? weightedSum / convertedFc : 0;

    const costRate = num(selectedInvoice?.weightedAvgCostRate)
                  || num(selectedOrder?.costRate)
                  || num(eefcContext?.costRate);

    const exchangeDiff = (weightedAvg - costRate) * convertedFc;

    return {
      fcToRealise, totalAllocated, remaining,
      convertedFc, eefcFc, weightedAvg, exchangeDiff, costRate
    };
  };

  const {
    fcToRealise, totalAllocated, remaining,
    convertedFc, eefcFc, weightedAvg, exchangeDiff, costRate
  } = calcTotals();

  const balanced = Math.abs(remaining) < 0.01;

  // Projected shortfall — only meaningful when fully settling a normal
  // invoice. Invoice FC less everything received across all rounds.
  const priorReceived = selectedInvoice
    ? records
        .filter(r => String(r.invoiceId) === String(selectedInvoice.invoiceId))
        .filter(r => String(r.realisationId) !== String(editingId))
        .reduce((s, r) => s + num(r.totalFcRealised), 0)
    : 0;
  const projectedShortfall = selectedInvoice
    ? Math.max(0, num(selectedInvoice.fcAmount)
        - priorReceived - num(form.totalFcRealised))
    : 0;

  const bankOptions = () => {
    const opts = [];
    if (form.bank && !banks.some(b => b.bankName === form.bank)) {
      opts.push(<option key="__old" value={form.bank}>{form.bank} (old)</option>);
    }
    banks.forEach(b => opts.push(
      <option key={b.bankId} value={b.bankName}>
        {b.bankName}{b.branch ? ' — ' + b.branch : ''}
      </option>));
    return opts;
  };

  // ------------------------------------------------------------------
  // save
  // ------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!form.customerCode)    { alert('Please select a customer'); return; }
    if (!form.realisationDate) { alert('Please enter the realisation date'); return; }
    if (!form.totalFcRealised) { alert('Please enter the total FC received'); return; }

    if (form.remittanceType === 'NORMAL' && !form.invoiceId) {
      alert('Please select an invoice'); return;
    }
    if (form.remittanceType === 'ADVANCE' && !form.orderId) {
      alert('Please select an order'); return;
    }

    // B16 — nothing may be negative.
    if (num(form.totalFcRealised) <= 0) {
      alert('Total FC received must be greater than zero.'); return;
    }
    if (num(form.spotFc) < 0 || num(form.eefcFc) < 0) {
      alert('Amounts cannot be negative.'); return;
    }
    if (forwardAllocations.some(a => num(a.fcAllocated) < 0) ||
        pcfcAllocations.some(a => num(a.fcAllocated) < 0)) {
      alert('Allocations cannot be negative.'); return;
    }

    if (num(form.spotFc) > 0 && num(form.spotRate) <= 0) {
      alert('Please enter the spot rate'); return;
    }

    if (num(form.eefcFc) > 0 && !form.eefcAccountId) {
      alert('Please choose the EEFC ledger the money is being kept in.'); return;
    }

    if (!balanced) {
      alert(
        'The allocation does not balance.\n\n'
      + `Spot, forward, PCFC, loan repayment and EEFC come to ${fmt(totalAllocated)}, `
      + `but ${fmt(fcToRealise)} was received.\n\n`
      + `Remaining to allocate: ${fmt(remaining)}`);
      return;
    }

    const payload = {
      remittanceType:    form.remittanceType,
      bank:              form.bank ? form.bank.trim() : null,
      invoiceId:         form.invoiceId ? parseInt(form.invoiceId) : null,
      orderId:           form.orderId   ? parseInt(form.orderId)   : null,
      realisationDate:   form.realisationDate,
      totalFcRealised:   num(form.totalFcRealised),
      spotFc:            form.spotFc   ? num(form.spotFc)   : null,
      spotRate:          form.spotRate ? num(form.spotRate) : null,
      loanRepaymentFc:   loanRepaymentFc > 0 ? loanRepaymentFc : null,
      loanRepaymentRate: form.loanRepaymentRate ? num(form.loanRepaymentRate) : null,

      eefcFc:        num(form.eefcFc) > 0 ? num(form.eefcFc) : null,
      eefcAccountId: num(form.eefcFc) > 0 && form.eefcAccountId
        ? parseInt(form.eefcAccountId) : null,
      benchmarkRate: form.benchmarkRate ? num(form.benchmarkRate) : null,

      isFinalSettlement: !!form.isFinalSettlement,
      notes:             form.notes || null,

      forwardAllocations: forwardAllocations
        .filter(a => num(a.fcAllocated) > 0)
        .map(a => ({ forwardId: a.forwardId, fcAllocated: num(a.fcAllocated) })),

      pcfcAllocations: pcfcAllocations
        .filter(a => num(a.fcAllocated) > 0)
        .map(a => ({ pcfcId: a.pcfcId, fcAllocated: num(a.fcAllocated) }))
    };

    try {
      setSaving(true);
      if (editingId) {
        await axios.put(
          `${CONFIG.API}/api/bills/realisations/${editingId}`, payload, auth);
      } else {
        await axios.post(`${CONFIG.API}/api/bills/realisations`, payload, auth);
      }
      resetForm();
      loadRecords();
    } catch (err) {
      alert(serverMessage(err, 'Could not save the realisation.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSettlementToggle = async (record) => {
    const newStatus = record.settlementStatus === 'SETTLED'
      ? 'NOT_SETTLED' : 'SETTLED';
    try {
      await axios.put(
        `${CONFIG.API}/api/bills/realisations/${record.realisationId}/settlement`,
        { settlementStatus: newStatus }, auth);
      loadRecords();
    } catch (err) {
      alert(serverMessage(err, 'Could not update the settlement status.'));
    }
  };

  // ------------------------------------------------------------------
  // editing
  // ------------------------------------------------------------------
  const startEdit = async (r) => {
    const rdate = String(r.realisationDate || '').slice(0, 10);
    const isNormal = r.remittanceType === 'NORMAL';

    try {
      // The detail first — it also says whether the entry is locked
      // because its EEFC money has been spent.
      const det = await axios.get(
        `${CONFIG.API}/api/bills/realisations/${r.realisationId}/detail`, auth);

      if (det.data.editLocked) {
        alert(det.data.editLockReason ||
          'This entry can no longer be edited because the money it parked in EEFC '
        + 'has already been used.');
        return;
      }

      const detFwd  = det.data.forwardAllocations || [];
      const detPcfc = det.data.pcfcAllocations || [];

      // 1. invoices / orders for this customer
      if (isNormal) {
        const inv = await axios.get(
          `${CONFIG.API}/api/bills/realisations/invoices/${r.customerCode}`, auth);
        setInvoices(inv.data);
        setOrders([]);
      } else {
        const ord = await axios.get(
          `${CONFIG.API}/api/bills/realisations/orders/${r.customerCode}`, auth);
        setOrders(ord.data);
        setInvoices([]);
      }

      // 2. discounting summary -> discounted FC + rate (normal only)
      let discountedFc = '';
      let loanRate = num(r.loanRepaymentRate) > 0 ? String(r.loanRepaymentRate) : '';
      if (isNormal && r.invoiceId) {
        try {
          const ds = await axios.get(
            `${CONFIG.API}/api/bills/realisations/discounting-summary/${r.invoiceId}`, auth);
          const fc = num(ds.data.fcDiscounted);
          if (fc > 0) {
            discountedFc = String(fc);
            if (!loanRate) loanRate = String(num(ds.data.discountRate));
          }
        } catch (e) { /* ignore */ }
      }

      // 3. available forwards at this bank
      let fwds = [];
      try {
        const url = isNormal
          ? `${CONFIG.API}/api/bills/realisations/available-forwards-date/${r.invoiceId}/${rdate}`
          : `${CONFIG.API}/api/bills/realisations/available-forwards-order/${r.orderId}/${rdate}`;
        const f = await axios.get(url, { ...auth, params: { bank: r.bank || '' } });
        fwds = readForwards(f.data).list;
      } catch (e) { /* ignore */ }

      // 4. available PCFC
      let pcfc = [];
      if (r.bank) {
        try {
          const url = isNormal
            ? `${CONFIG.API}/api/bills/realisations/available-pcfc/${r.invoiceId}`
            : `${CONFIG.API}/api/bills/realisations/available-pcfc-order/${r.orderId}`;
          const p = await axios.get(url, { ...auth, params: { bank: r.bank } });
          pcfc = p.data;
        } catch (e) { /* ignore */ }
      }

      // 5. merge this record's own allocations back into availability,
      //    so they still show (they were used up by this very record)
      detFwd.forEach(d => {
        const ex = fwds.find(f => f.forwardId === d.forwardId);
        if (ex) ex.fcAvailable = num(ex.fcAvailable) + num(d.fcAllocated);
        else fwds.push({
          forwardId: d.forwardId, dealNo: d.dealNo, bank: d.bank,
          forwardRate: d.forwardRate, fcAvailable: num(d.fcAllocated),
          windowStartDate: d.windowStartDate, windowEndDate: d.windowEndDate
        });
      });
      detPcfc.forEach(d => {
        const ex = pcfc.find(p => p.pcfcId === d.pcfcId);
        if (ex) ex.fcAvailable = num(ex.fcAvailable) + num(d.fcAllocated);
        else pcfc.push({
          pcfcId: d.pcfcId, pcfcRef: d.pcfcRef, pcfcDate: d.pcfcDate,
          drawdownRate: d.drawdownRate, fcAvailable: num(d.fcAllocated),
          bank: d.bank, currency: d.currency
        });
      });

      setAvailableForwards(fwds);
      setForwardReason(null);
      setAvailablePcfc(pcfc);

      setForwardAllocations(detFwd.map(d => ({
        forwardId: d.forwardId,
        fcAllocated: String(d.fcAllocated),
        forwardRate: d.forwardRate
      })));
      setPcfcAllocations(detPcfc.map(d => ({
        pcfcId: d.pcfcId,
        fcAllocated: String(d.fcAllocated)
      })));

      // 6. EEFC context
      await loadEefcContext(
        isNormal ? r.invoiceId : null,
        isNormal ? null : r.orderId,
        r.bank || '', rdate);

      const eefcAmount = num(det.data.eefcFc);

      setForm({
        remittanceType:   r.remittanceType,
        customerCode:     r.customerCode,
        bank:             r.bank || '',
        invoiceId:        r.invoiceId ? String(r.invoiceId) : '',
        orderId:          r.orderId   ? String(r.orderId)   : '',
        realisationDate:  rdate,
        totalFcRealised:  r.totalFcRealised != null ? String(r.totalFcRealised) : '',
        spotFc:           num(r.spotFc)   > 0 ? String(r.spotFc)   : '',
        spotRate:         num(r.spotRate) > 0 ? String(r.spotRate) : '',
        discountedFc,
        loanRepaymentRate: loanRate,
        eefcFc:           eefcAmount > 0 ? String(eefcAmount) : '',
        eefcAccountId:    det.data.eefcAccountId ? String(det.data.eefcAccountId) : '',
        benchmarkRate:    '',
        isFinalSettlement: r.settlementStatus === 'SETTLED',
        notes:            r.notes || ''
      });

      setEditingId(r.realisationId);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      alert(serverMessage(err, 'Could not load this record for editing.'));
    }
  };

  const handleDelete = async (r) => {
    const label = r.invoiceNo !== 'ADVANCE' ? r.invoiceNo : r.orderNo;
    const ok = window.confirm(
      `Delete this realisation for ${r.customerCode} (${label})?\n\n`
    + 'This reverses the PCFC repayment, frees the forward contracts, removes any '
    + 'money it parked in EEFC, and reopens the invoice. This cannot be undone.');
    if (!ok) return;
    try {
      await axios.delete(
        `${CONFIG.API}/api/bills/realisations/${r.realisationId}`, auth);
      if (editingId === r.realisationId) resetForm();
      loadRecords();
    } catch (err) {
      alert(serverMessage(err, 'Could not delete this realisation.'));
    }
  };

  // ------------------------------------------------------------------
  // the allocation section — used for both normal and advance
  // ------------------------------------------------------------------
  const renderAllocationSection = (heading) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '13px', fontWeight: '600',
                    color: '#374151', marginBottom: '10px' }}>
        {heading}
      </div>

      {/* Spot */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: '12px', marginBottom: '6px' }}>
        <div className="form-field">
          <label>Spot — FC amount</label>
          <input type="number" min="0" name="spotFc" value={form.spotFc}
            onChange={handleChange} placeholder="0" />
        </div>
        <div className="form-field">
          <label>Spot rate</label>
          <input type="number" min="0" name="spotRate" value={form.spotRate}
            onChange={handleChange} placeholder="e.g. 84.50" />
        </div>
      </div>
      {num(form.spotFc) > 0 && num(form.spotRate) > 0 && (
        <div style={{ ...noteStyle, marginBottom: '8px', paddingLeft: '4px' }}>
          Spot INR = {fmt0(num(form.spotFc) * num(form.spotRate))}
        </div>
      )}

      {/* Forward table */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{ fontSize: '12px', fontWeight: '600',
                      color: '#374151', marginBottom: '6px' }}>
          Forward contracts at {form.bank || 'this bank'} covering the realisation date
        </div>

        {availableForwards.length === 0 ? (
          <div style={warnBox}>
            {forwardReason || 'No forward contracts match.'}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Deal no</th>
                <th>Bank</th>
                <th>Forward rate</th>
                <th>FC available</th>
                <th>Window</th>
                <th>Allocate FC</th>
                <th>INR value</th>
              </tr>
            </thead>
            <tbody>
              {availableForwards.map(fwd => {
                const alloc   = forwardAllocations.find(
                  a => a.forwardId === fwd.forwardId);
                const allocFc = num(alloc?.fcAllocated);
                return (
                  <tr key={fwd.forwardId}>
                    <td>{fwd.dealNo}</td>
                    <td>{fwd.bank}</td>
                    <td>{rate4(fwd.forwardRate)}</td>
                    <td>{fmt(fwd.fcAvailable)}</td>
                    <td style={{ fontSize: '11px' }}>
                      {fwd.windowStartDate} to {fwd.windowEndDate}
                    </td>
                    <td>
                      <input type="number" min="0"
                        value={alloc?.fcAllocated || ''}
                        onChange={e => handleForwardAllocationChange(
                          fwd.forwardId, e.target.value)}
                        placeholder="0" max={fwd.fcAvailable}
                        style={smallInput} />
                    </td>
                    <td style={{ color: '#166534', fontWeight: '600' }}>
                      {allocFc > 0 ? fmt0(allocFc * num(fwd.forwardRate)) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* PCFC repayment */}
      <div style={{ marginTop: '6px' }}>
        <div style={{ fontSize: '12px', fontWeight: '600',
                      color: '#374151', marginBottom: '6px' }}>
          PCFC repayment (optional) — oldest loans first
        </div>

        {!form.bank && (
          <div style={warnBox}>Select a bank above to see its PCFC loans.</div>
        )}

        {form.bank && availablePcfc.length === 0 && (
          <div style={warnBox}>
            No open PCFC loans at {form.bank} in {pairLabel(billCurrency)}.
          </div>
        )}

        {availablePcfc.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>PCFC ref</th>
                <th>Date</th>
                <th>Draw rate</th>
                <th>FC outstanding</th>
                <th>Repay FC</th>
              </tr>
            </thead>
            <tbody>
              {availablePcfc.map(p => {
                const alloc = pcfcAllocations.find(a => a.pcfcId === p.pcfcId);
                return (
                  <tr key={p.pcfcId}>
                    <td>{p.pcfcRef}</td>
                    <td>{p.pcfcDate}</td>
                    <td>{rate4(p.drawdownRate)}</td>
                    <td>{fmt(p.fcAvailable)}</td>
                    <td>
                      <input type="number" min="0"
                        value={alloc?.fcAllocated || ''}
                        onChange={e => handlePcfcAllocationChange(
                          p.pcfcId, e.target.value)}
                        placeholder="0" max={p.fcAvailable}
                        style={smallInput} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Keep in EEFC */}
      <div style={{ marginTop: '18px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600',
                      color: '#374151', marginBottom: '4px' }}>
          Keep in EEFC (optional)
        </div>
        <div style={{ ...noteStyle, marginBottom: '8px' }}>
          Foreign currency you have received but are not converting today. It is parked
          as a parcel that remembers this bill and its cost rate. The bill is settled by
          it either way — it is converted later from the EEFC page, when the rate suits
          you.
        </div>

        {!form.bank && (
          <div style={warnBox}>
            Select a bank above — an EEFC ledger belongs to one bank and one currency.
          </div>
        )}

        {form.bank && eefcContext && eefcContext.accounts?.length === 0 && (
          <div style={warnBox}>
            {form.bank} has no {pairLabel(eefcContext.currency)} EEFC ledger yet.
            {eefcContext.bankId ? (
              <div style={{ marginTop: '8px' }}>
                <button className="btn-secondary"
                  onClick={handleCreateLedger} disabled={creatingLedger}>
                  {creatingLedger
                    ? 'Creating...'
                    : `+ Create the ${pairLabel(eefcContext.currency)} ledger at ${form.bank}`}
                </button>
              </div>
            ) : (
              <div style={{ marginTop: '6px' }}>
                {eefcContext.bankMatches > 1
                  ? 'More than one bank has this name, so the ledger has to be created '
                  + 'on the Bank page against the right branch.'
                  : 'Add it on the Bank page first.'}
              </div>
            )}
          </div>
        )}

        {form.bank && eefcContext && eefcContext.accounts?.length > 0 && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                          gap: '12px', marginBottom: '6px' }}>
              <div className="form-field">
                <label>EEFC — FC amount to park</label>
                <input type="number" min="0" name="eefcFc" value={form.eefcFc}
                  onChange={handleChange} placeholder="0" />
              </div>
              <div className="form-field">
                <label>EEFC ledger</label>
                <select name="eefcAccountId" value={form.eefcAccountId}
                  onChange={handleChange}>
                  <option value="">— Select ledger —</option>
                  {eefcContext.accounts.map(a => (
                    <option key={a.accountId} value={a.accountId}>
                      {a.bankName} — {pairLabel(a.currency)}
                      {a.accountNo ? ` (${a.accountNo})` : ''} — balance {fmt(a.balanceFc)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {num(form.eefcFc) > 0 && (
              <div style={{ ...infoBox, marginTop: '8px', marginBottom: '4px' }}>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  <span><strong>Cost rate of the parcel:</strong> {rate4(costRate)}</span>
                  <span>
                    <strong>Market rate on arrival:</strong>{' '}
                    {num(form.spotFc) > 0 && num(form.spotRate) > 0
                      ? `${rate4(form.spotRate)} (your spot rate today)`
                      : num(eefcContext.benchmarkRate) > 0
                        ? `${rate4(eefcContext.benchmarkRate)} (close of ${eefcContext.benchmarkDate})`
                        : 'not available'}
                  </span>
                </div>
                <div style={{ ...noteStyle, marginTop: '6px' }}>
                  These two rates are what the profit is measured against later — what the
                  export was priced at, and what the market was worth the day the money
                  arrived.
                </div>
              </div>
            )}

            {/* Only needed when rate history has nothing for that day */}
            {num(form.eefcFc) > 0 &&
             !(num(form.spotFc) > 0 && num(form.spotRate) > 0) &&
             num(eefcContext.benchmarkRate) === 0 && (
              <>
                <div style={warnBox}>
                  There is no market rate for {pairLabel(eefcContext.currency)} on or before
                  {' '}{form.realisationDate || 'this date'}. Either upload it on the Rates
                  page, or type it below.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                              gap: '12px', marginBottom: '6px' }}>
                  <div className="form-field">
                    <label>Market rate on the realisation date</label>
                    <input type="number" min="0" name="benchmarkRate"
                      value={form.benchmarkRate} onChange={handleChange}
                      placeholder="e.g. 84.20" />
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Read-only loan repayment block (auto-filled from the discounting)
  const renderLoanRepayment = (heading) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '13px', fontWeight: '600',
                    color: '#374151', marginBottom: '10px' }}>
        {heading}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div className="form-field">
          <label>Loan repayment FC (auto)</label>
          <input value={loanRepaymentFc || ''} readOnly
            style={{ background: '#F1F5F9', color: '#475569' }} />
        </div>
        <div className="form-field">
          <label>Rate — discounted at (auto)</label>
          <input value={form.loanRepaymentRate} readOnly
            style={{ background: '#F1F5F9', color: '#475569' }} />
        </div>
      </div>
      <div style={{ ...noteStyle, marginTop: '6px', paddingLeft: '4px' }}>
        This closes the discounted portion at the rate it was discounted at — no new
        exchange difference.
      </div>
    </div>
  );

  const filteredRecords = records.filter(r => {
    const matchCustomer   = filterCustomer === '' ||
      String(r.customerCode || '').toLowerCase().includes(filterCustomer.toLowerCase());
    const matchSettlement = filterSettlement === '' ||
      r.settlementStatus === filterSettlement;
    return matchCustomer && matchSettlement;
  });

  const totalParked = records.reduce((s, r) => s + num(r.eefcFc), 0);

  return (
    <div className="content">

      {/* ============================================================ */}
      {/* Summary cards                                                */}
      {/* ============================================================ */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Total realisations</div>
          <div className="metric-value val-blue">{records.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Settled</div>
          <div className="metric-value val-green">
            {records.filter(r => r.settlementStatus === 'SETTLED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Not settled</div>
          <div className="metric-value val-amber">
            {records.filter(r => r.settlementStatus === 'NOT_SETTLED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Parked in EEFC</div>
          <div className="metric-value val-blue">
            {totalParked > 0 ? fmt0(totalParked) : '—'}
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* Header                                                       */}
      {/* ============================================================ */}
      <div className="section-header">
        <div className="section-title">Realisation records</div>
        <button className="btn-primary" onClick={() => {
          if (showForm) { resetForm(); } else { setForm(emptyForm); setShowForm(true); }
        }}>
          {showForm ? '✕ Close' : '+ New realisation'}
        </button>
      </div>

      {/* ============================================================ */}
      {/* Form                                                         */}
      {/* ============================================================ */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '900px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingId ? '✏️ Edit realisation' : '📋 New realisation entry'}
          </div>

          {/* Row 1 — type + customer + bank */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Remittance type</label>
              <select name="remittanceType" value={form.remittanceType}
                onChange={handleChange} disabled={!!editingId}>
                <option value="NORMAL">Normal realisation</option>
                <option value="ADVANCE">Advance remittance</option>
              </select>
            </div>
            <div className="form-field">
              <label>Customer code</label>
              <select name="customerCode" value={form.customerCode}
                onChange={handleChange} disabled={!!editingId}>
                <option value="">— Select customer —</option>
                {customers.map(c => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <div className="form-field">
              <label>Bank</label>
              <select name="bank" value={form.bank} onChange={handleChange}>
                <option value="">— Select bank —</option>
                {bankOptions()}
              </select>
            </div>
          </div>

          {/* Row 2 — invoice/order + date + total */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>{form.remittanceType === 'NORMAL' ? 'Invoice' : 'Order'}</label>
              {form.remittanceType === 'NORMAL' ? (
                <select name="invoiceId" value={form.invoiceId}
                  onChange={handleChange} disabled={!!editingId}>
                  <option value="">— Select invoice —</option>
                  {invoices.map(inv => (
                    <option key={inv.invoiceId} value={inv.invoiceId}>
                      {inv.invoiceNo} — {ccyCode(inv.currency)} {fmt0(inv.fcOpen)} open
                    </option>
                  ))}
                </select>
              ) : (
                <select name="orderId" value={form.orderId}
                  onChange={handleChange} disabled={!!editingId}>
                  <option value="">— Select order —</option>
                  {orders.map(ord => (
                    <option key={ord.orderId} value={ord.orderId}>
                      {ord.orderNo} — {ccyCode(ord.currency)} {fmt0(ord.fcAmount)}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-field">
              <label>Realisation date</label>
              <input type="date" name="realisationDate"
                value={form.realisationDate} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Total FC received</label>
              <input type="number" min="0" name="totalFcRealised"
                value={form.totalFcRealised} onChange={handleChange}
                placeholder="0" />
            </div>
          </div>

          <div style={{ ...noteStyle, marginBottom: '14px' }}>
            Forward contracts, PCFC loans and EEFC ledgers are all matched to the bank you
            pick here. Changing the bank reloads all three.
          </div>

          {/* Invoice details bar */}
          {selectedInvoice && (
            <div style={{ ...infoBox, display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <span><strong>Currency:</strong> {pairLabel(selectedInvoice.currency)}</span>
              <span><strong>FC amount:</strong> {fmt(selectedInvoice.fcAmount)}</span>
              <span><strong>FC open:</strong> {fmt(selectedInvoice.fcOpen)}</span>
              <span><strong>FC discounted:</strong> {fmt(selectedInvoice.fcDiscounted)}</span>
              <span><strong>Due date:</strong> {selectedInvoice.dueDate}</span>
              <span><strong>Cost rate:</strong> {rate4(selectedInvoice.weightedAvgCostRate)}</span>
              <span style={{
                fontWeight: '700',
                color: isFullyDiscounted ? '#166534'
                     : isPartlyDiscounted ? '#92400E' : '#1D4ED8'
              }}>
                {isFullyDiscounted ? '✅ Fully discounted'
                 : isPartlyDiscounted ? '⚠️ Partly discounted'
                 : '🔵 Not discounted'}
              </span>
            </div>
          )}

          {selectedOrder && (
            <div style={{ ...infoBox, display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <span><strong>Order FC:</strong> {fmt(selectedOrder.fcAmount)}</span>
              <span><strong>Currency:</strong> {pairLabel(selectedOrder.currency)}</span>
              <span><strong>Cost rate:</strong> {rate4(selectedOrder.costRate)}</span>
              {num(selectedOrder.advanceExpectedFc) > 0 && (
                <span><strong>Advance expected:</strong> {fmt(selectedOrder.advanceExpectedFc)}</span>
              )}
            </div>
          )}

          {/* B14 — a bill with no currency, said plainly */}
          {((selectedInvoice && !selectedInvoice.currency) ||
            (selectedOrder && !selectedOrder.currency)) && (
            <div style={warnBox}>
              This bill has no currency recorded, so no forward contract, PCFC loan or EEFC
              ledger can be matched to it. Set its currency first.
            </div>
          )}

          {/* ---- scenario sections ---- */}

          {/* Fully discounted — loan repayment only */}
          {form.remittanceType === 'NORMAL' && selectedInvoice && isFullyDiscounted &&
            renderLoanRepayment('Discounted portion — loan repayment')}

          {/* Partly discounted — loan repayment + open portion */}
          {form.remittanceType === 'NORMAL' && selectedInvoice && isPartlyDiscounted && (
            <>
              {renderLoanRepayment('Discounted portion — loan repayment')}
              {renderAllocationSection('Open portion — Spot / Forward / PCFC / EEFC')}
            </>
          )}

          {/* Not discounted, or advance — full allocation */}
          {(form.remittanceType === 'ADVANCE' ||
            (form.remittanceType === 'NORMAL' && selectedInvoice && isNotDiscounted)) &&
            renderAllocationSection('Allocation — Spot / Forward / PCFC / EEFC')}

          {/* Fully settled (final payment) */}
          {form.remittanceType === 'NORMAL' && selectedInvoice && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '10px 14px', marginBottom: '14px',
              border: '1px solid #CBD5E1', borderRadius: '6px',
              background: form.isFinalSettlement ? '#F0FDF4' : '#FFFFFF'
            }}>
              <input type="checkbox" checked={form.isFinalSettlement}
                onChange={e => setForm({ ...form, isFinalSettlement: e.target.checked })}
                style={{ width: '16px', height: '16px' }} />
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Fully settled (final payment)
              </span>
              <span style={{ ...noteStyle, marginLeft: 'auto' }}>
                {form.isFinalSettlement
                  ? `Invoice will be closed. Shortfall: ${fmt(projectedShortfall)}`
                  : 'Leave unticked if more payment is still expected'}
              </span>
            </div>
          )}

          {/* Balance bar */}
          {fcToRealise > 0 && (
            <div style={{
              background: balanced ? '#F0FDF4' : '#FEF2F2',
              border: `1px solid ${balanced ? '#BBF7D0' : '#FCA5A5'}`,
              borderRadius: '6px', padding: '10px 14px',
              fontSize: '13px', marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <span><strong>FC received:</strong> {fmt(fcToRealise)}</span>
                <span><strong>Total allocated:</strong> {fmt(totalAllocated)}</span>
                <span style={{
                  color: balanced ? '#166534' : '#DC2626', fontWeight: '700'
                }}>
                  <strong>Remaining:</strong> {fmt(remaining)}
                </span>
                {eefcFc > 0 && (
                  <span style={{ color: '#7C3AED', fontWeight: '600' }}>
                    <strong>Kept in EEFC:</strong> {fmt(eefcFc)} {ccyCode(billCurrency)}
                  </span>
                )}
                {weightedAvg > 0 && (
                  <span style={{ color: '#1D4ED8', fontWeight: '600' }}>
                    <strong>Wtd avg rate{eefcFc > 0 ? ' (converted part)' : ''}:</strong>{' '}
                    {weightedAvg.toFixed(4)}
                  </span>
                )}
                {weightedAvg > 0 && costRate > 0 && (
                  <span style={{
                    color: exchangeDiff >= 0 ? '#166534' : '#DC2626', fontWeight: '600'
                  }}>
                    <strong>Ex diff:</strong> ₹{fmt0(exchangeDiff)}
                  </span>
                )}
              </div>
              {eefcFc > 0 && (
                <div style={{ ...noteStyle, marginTop: '6px' }}>
                  The rate and the exchange difference cover the {fmt(convertedFc)} that was
                  actually converted. The {fmt(eefcFc)} kept in EEFC settles the bill just
                  the same, but has no rate yet — its profit or loss appears when it leaves
                  the ledger.
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea name="notes" value={form.notes} onChange={handleChange}
                rows={2} placeholder="Any additional notes..."
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }} />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...'
                : (editingId ? '✓ Update realisation' : '✓ Save realisation')}
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* Filter bar                                                   */}
      {/* ============================================================ */}
      <div style={{
        display: 'flex', gap: '12px', alignItems: 'center',
        marginBottom: '12px', flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="🔍 Search customer..."
          value={filterCustomer}
          onChange={e => setFilterCustomer(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', width: '200px'
          }} />
        <select
          value={filterSettlement}
          onChange={e => setFilterSettlement(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', color: '#374151'
          }}>
          <option value="">All statuses</option>
          <option value="SETTLED">Settled</option>
          <option value="NOT_SETTLED">Not settled</option>
        </select>
        {(filterSettlement !== '' || filterCustomer !== '') && (
          <button
            onClick={() => { setFilterSettlement(''); setFilterCustomer(''); }}
            style={{
              padding: '7px 12px', border: '1px solid #CBD5E1',
              borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              background: '#F1F5F9', color: '#64748B'
            }}>
            ✕ Clear filters
          </button>
        )}
        <span style={{ fontSize: '13px', color: '#64748B', marginLeft: 'auto' }}>
          Showing {filteredRecords.length} of {records.length} records
        </span>
      </div>

      {/* ============================================================ */}
      {/* Records table                                                */}
      {/* ============================================================ */}
      <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : filteredRecords.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No realisation records found.
          </p>
        ) : (
          <table className="data-table" style={{ minWidth: '1600px' }}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Invoice / Order</th>
                <th>Invoice amount</th>
                <th>Customer</th>
                <th>Bank</th>
                <th>Currency</th>
                <th>Realisation date</th>
                <th>FC realised</th>
                <th>In EEFC</th>
                <th>Wtd avg rate</th>
                <th>Cost rate</th>
                <th>Ex diff ₹</th>
                <th>Shortfall FC</th>
                <th>Shortfall ₹</th>
                <th>Due date</th>
                <th>Early/Delay</th>
                <th>Settlement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map(r => (
                <tr key={r.realisationId}>
                  <td>
                    <span className={`badge ${r.remittanceType === 'ADVANCE'
                      ? 'badge-partial' : 'badge-open'}`}>
                      {r.remittanceType === 'ADVANCE' ? 'ADVANCE' : 'NORMAL'}
                    </span>
                  </td>
                  <td>{r.invoiceNo !== 'ADVANCE' ? r.invoiceNo : r.orderNo}</td>
                  <td>{num(r.invoiceFcAmount) > 0 ? fmt(r.invoiceFcAmount) : '—'}</td>
                  <td>{r.customerCode}</td>
                  <td>{r.bank || '—'}</td>
                  <td>{ccyCode(r.currency)}</td>
                  <td>{String(r.realisationDate || '').slice(0, 10)}</td>
                  <td>{fmt(r.totalFcRealised)}</td>
                  <td style={{ color: num(r.eefcFc) > 0 ? '#7C3AED' : '#94A3B8',
                               fontWeight: num(r.eefcFc) > 0 ? '600' : '400' }}>
                    {num(r.eefcFc) > 0 ? fmt0(r.eefcFc) : '—'}
                  </td>
                  <td>{rate4(r.weightedAvgRate)}</td>
                  <td>{rate4(r.costRate)}</td>
                  <td style={{
                    color: num(r.exchangeDiff) >= 0 ? '#166534' : '#DC2626',
                    fontWeight: '600'
                  }}>
                    {num(r.weightedAvgRate) > 0 ? fmt0(r.exchangeDiff) : '—'}
                  </td>
                  <td>{num(r.shortfallFc) > 0 ? fmt(r.shortfallFc) : '—'}</td>
                  <td style={{ color: '#DC2626' }}>
                    {num(r.shortfallInr) > 0 ? fmt0(r.shortfallInr) : '—'}
                  </td>
                  <td>{r.dueDate || '—'}</td>
                  <td>
                    {num(r.daysEarly) > 0 && (
                      <span style={{ color: '#166534', fontSize: '12px' }}>
                        ⬆ {r.daysEarly}d early
                      </span>
                    )}
                    {num(r.daysDelayed) > 0 && (
                      <span style={{ color: '#DC2626', fontSize: '12px' }}>
                        ⬇ {r.daysDelayed}d late
                      </span>
                    )}
                    {!num(r.daysEarly) && !num(r.daysDelayed) && r.dueDate && (
                      <span style={{ color: '#166534', fontSize: '12px' }}>✓ On time</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => handleSettlementToggle(r)}
                      style={{
                        padding: '4px 10px', borderRadius: '4px', border: 'none',
                        cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                        background: r.settlementStatus === 'SETTLED' ? '#DCFCE7' : '#FEF9C3',
                        color: r.settlementStatus === 'SETTLED' ? '#166534' : '#92400E'
                      }}>
                      {r.settlementStatus === 'SETTLED' ? '✓ Settled' : '⏳ Not settled'}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => startEdit(r)}
                        title="Edit this realisation"
                        style={{
                          padding: '4px 10px', borderRadius: '4px',
                          border: '1px solid #BAE6FD', cursor: 'pointer',
                          fontSize: '12px', fontWeight: '600',
                          background: '#F0F9FF', color: '#1D4ED8'
                        }}>
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(r)}
                        title="Delete and reverse this realisation"
                        style={{
                          padding: '4px 10px', borderRadius: '4px',
                          border: '1px solid #FCA5A5', cursor: 'pointer',
                          fontSize: '12px', fontWeight: '600',
                          background: '#FEF2F2', color: '#DC2626'
                        }}>
                        🗑 Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalParked > 0 && (
        <div style={{ ...noteStyle, marginTop: '10px' }}>
          The "In EEFC" column is money received but not yet converted. The bill is settled
          by it either way. Its profit or loss appears on the EEFC page, on the day it
          finally leaves the ledger.
        </div>
      )}

    </div>
  );
}

export default RealisationsPage;